import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useBranding } from '../core/branding/BrandingContext';
import { useLandUseState } from '../features/climateplus/landuse-optimizer/hooks/useLandUseState';
import { LandUseMap } from '../features/climateplus/landuse-optimizer/components/LandUseMap';
import { LegendBar } from '../features/climateplus/landuse-optimizer/components/LegendBar';
import { ParcelEditor } from '../features/climateplus/landuse-optimizer/components/ParcelEditor';
import { MetricsPanel } from '../features/climateplus/landuse-optimizer/components/MetricsPanel';
import { ClassBreakdownTable } from '../features/climateplus/landuse-optimizer/components/ClassBreakdownTable';
import { OptimizerPanel } from '../features/climateplus/landuse-optimizer/components/OptimizerPanel';
import { DirtyStateBanner } from '../features/climateplus/landuse-optimizer/components/DirtyStateBanner';

export const LandUseOptimizerPage: React.FC = () => {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const {
    seed,
    isLoading,
    workingParcels,
    workingSoil,
    selectedParcelId,
    changedByOptimizer,
    isDirty,
    metrics,
    setSelectedParcelId,
    handleParcelReassign,
    handleSoilToggle,
    handleRecompute,
    handleReset,
    handleNewScenario,
    handleRunOptimizer,
  } = useLandUseState();

  const selectedParcel = workingParcels.find((p) => p.id === selectedParcelId);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-700 text-lg font-semibold">Loading Land Use Data...</p>
          <p className="text-gray-500 text-sm mt-2">Loading {workingParcels.length || 0} parcels from Naas region</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header
        className="flex items-center justify-between px-4 py-2 shadow-md z-10"
        style={{ backgroundColor: branding?.primaryColor || '#2563eb' }}
      >
        <div className="flex items-center space-x-3">
          {branding?.logoUrl && (
            <img
              src={branding.logoUrl}
              alt="Logo"
              className="h-7 object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
          <h1 className="text-base font-semibold text-white">Land Use Optimizer</h1>
        </div>

        <button
          onClick={() => navigate('/dashboard')}
          className="px-3 py-1.5 bg-white rounded text-xs font-semibold hover:bg-gray-100 transition-colors"
          style={{ color: branding?.primaryColor || '#2563eb' }}
        >
          Back to Dashboard
        </button>
      </header>

      {/* Subheader */}
      <div className="bg-white border-b border-gray-200 px-4 py-2">
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-2">
          <div className="text-[10px] text-gray-500 font-mono">
            TEST PLOT 53.35N 6.45W · 2.0km × 2.0km (400 ha)
          </div>
          <div className="flex items-center gap-2">
            <div className="text-[10px] text-gray-500 font-mono">SCENARIO #{seed}</div>
            <button
              onClick={handleReset}
              className="flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
              title="Reset to original data"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Main Content - Tighter spacing */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left Column - Map */}
          <div className="lg:col-span-2 space-y-3">
            <LandUseMap
              parcels={workingParcels}
              soilZones={workingSoil}
              selectedParcelId={selectedParcelId}
              changedByOptimizer={changedByOptimizer}
              onParcelClick={setSelectedParcelId}
              onSoilToggle={handleSoilToggle}
            />

            <LegendBar showChangedLegend={changedByOptimizer.size > 0} />

            <ParcelEditor
              selectedParcel={selectedParcel}
              soilZones={workingSoil}
              onReassign={handleParcelReassign}
            />
          </div>

          {/* Right Column - Panels */}
          <div className="space-y-3">
            <MetricsPanel
              metrics={metrics}
              isDirty={isDirty}
              seed={seed}
              onRecompute={handleRecompute}
              onNewScenario={handleNewScenario}
            />

            <DirtyStateBanner show={isDirty} />

            <ClassBreakdownTable classRows={metrics.classRows} />

            <OptimizerPanel onRun={handleRunOptimizer} />
          </div>
        </div>
      </div>
    </div>
  );
};